import React from 'react';
import { Navigate } from 'react-router-dom';
import { BsFillArchiveFill, BsPencilSquare, BsPlus } from 'react-icons/bs';
import { Server } from '../../server/server';
import AlertModal from '../modals/AlertModal';
import BadWords from '../util/badWords';
import PageEnvelope from '../elements/PageEnvelope';
import PageHeader from '../elements/PageHeader';
import './SitePage.css'

interface ForumPageState {
  loading: boolean;
  loadingMore: boolean;
  forum: any;
  topics: any[];
  alert: string;
  navigate: string;
  noMoreTopics: boolean;
  loadError: boolean;
}

class ForumPage extends React.Component<{}, ForumPageState> {
  topicsPerPage: number;

  constructor(props: {}) {
    super(props);
    this.state = {
      loading: false,
      loadingMore: false,
      forum: null,
      topics: [],
      alert: '',
      navigate: '',
      noMoreTopics: false,
      loadError: false
    };

    this.topicsPerPage = 10;
  }

  componentDidMount() {
    this.load();
  }

  getForumId() {
    let i = window.location.pathname.lastIndexOf('/');
    return window.location.pathname.substring(i+1);
  }

  async load() {
    let success = await this.loadForum();

    if(!success)
      return;

    await this.loadTopics();
  }

  async loadForum() {
    let forumId = this.getForumId();
    let forums = await Server.forums.getForumsCache();
    let forum = forums ? forums.find((f:any) => f.id == forumId) : null;

    if(!forum) {
      this.setState({loading: true});
      let forumResponse = await Server.forums.getForum(forumId);
      console.log(forumResponse);
      
      if(!forumResponse.success) {
        this.setState({loading: false, loadError: true});
        return false;
      }

      forum = forumResponse.forum;
    }

    this.setState({forum: forum})

    return true;
  }

  async loadTopics() {
    let cachedTopics = await Server.forums.getTopicsCache(this.getForumId());

    let updating = false;
    if(cachedTopics) {
      let profiles = this.getProfilesInTopics(cachedTopics);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) {
        this.setState({loading: true});
        await Server.public.loadProfiles(profiles);
      }

      this.setState({topics: cachedTopics, noMoreTopics: cachedTopics.length < 10});
      
      updating = cachedTopics.length > 0;
      let forumRefreshTime = window.sessionStorage.getItem(`ForumRefresh#${this.getForumId()}`);
      if(forumRefreshTime) {
        let refreshTime = parseInt(forumRefreshTime);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) {
          this.setState({loading: false});
          return;
        }
      }
    }

    if(!updating)
      this.setState({loading: true});

    let params:any = {limit: this.topicsPerPage};

    if(updating)
      params.after = {date: cachedTopics[0].recent.date, author: cachedTopics[0].recent.author};

    let response = await Server.forums.getTopics(this.getForumId(), params);

    if(!response.success) {
      this.setState({loading: false, alert: response.message});
      return;
    }

    window.sessionStorage.setItem(`ForumRefresh#${this.getForumId()}`, new Date().getTime().toString());
    
    let newTopics = response.topics;

    if(newTopics.length == 0 && updating) {
      this.setState({loading: false});
      return;
    }

    let profiles = this.getProfilesInTopics(newTopics);
    await Server.public.loadProfiles(profiles);

    // building new list of topics
    let topics = [];
    if(updating && newTopics.length < 10)
      topics = [...cachedTopics];

    topics = await this.addNewTopicsToExistingTopics(newTopics, topics);

    await Server.forums.setTopicsCache(this.getForumId(), topics, updating);

    if(!updating) {
      let noMoreTopics = topics.length < this.topicsPerPage;
      this.setState({noMoreTopics});
    }

    this.setState({topics, loading: false});
  }

  async loadMoreTopics() {
    this.setState({loadingMore: true});

    let params:any = {limit: this.topicsPerPage};

    let post = this.state.topics[this.state.topics.length-1];
    params.before = {date: post.recent.date, author: post.recent.author};

    let response = await Server.forums.getTopics(this.getForumId(), params);

    if(!response.success) {
      this.setState({loadingMore: false, alert: response.message});
      return;
    }

    let newTopics = response.topics;

    if(newTopics.length == 0) {
      this.setState({loadingMore: false, noMoreTopics: true});
      return;
    }

    let profiles = this.getProfilesInTopics(newTopics);
    await Server.public.loadProfiles(profiles);

    let topics = await this.addNewTopicsToExistingTopics(newTopics, this.state.topics);
    
    await Server.forums.setTopicsCache(this.getForumId(), topics, true);

    let noMoreTopics = topics.length < this.topicsPerPage;

    this.setState({topics, noMoreTopics, loadingMore: false});
  }

  async addNewTopicsToExistingTopics(newTopics:any[], existingTopics:any[]) {
    let topicIds = newTopics.map((topic:any) => topic.id);
    let missingTopicIds = topicIds.filter((id: string) => !existingTopics.some((topic: any) => topic.id === id));
    
    if(missingTopicIds.length > 0) {
      let infoResponse = await Server.forums.getTopicsInfo(this.getForumId(), missingTopicIds);
      if(!infoResponse.success) {
        this.setState({loading: false, alert: infoResponse.message});
        return;
      }
      if(infoResponse.topics.length > 0)
        existingTopics = existingTopics.concat(infoResponse.topics);
    }

    for(let i = 0; i < existingTopics.length; i++) {
      let newTopic = newTopics.find((t:any) => t.id === existingTopics[i].id);
      if(newTopic && newTopic.recent) 
        existingTopics[i].recent = newTopic.recent;
    }

    existingTopics.sort((a:any, b:any) => new Date(b.recent.date).getTime() - new Date(a.recent.date).getTime());

    return existingTopics;
  }

  getProfilesInTopics(topics:any[]) {
    let profiles = [];
    for (let i = 0; i < topics.length; i++) {
      let topic = topics[i];

      if(profiles.indexOf(topic.author) == -1)
        profiles.push(topic.author);

      if(topic.recent) {
        if(profiles.indexOf(topic.recent.author) == -1)
          profiles.push(topic.recent.author);
      }
    }
    return profiles;
  }

  onViewTopic(topicId:string) {
    // this.saveState();
    this.setState({navigate: `/forum/${this.state.forum.id}/${topicId}`});
  }

  onNewTopic() {
    if(this.state.loading)
      return;

    if(!Server.account.isLoggedIn()) {
      this.setState({alert: 'Please login to start a new topic.'});
      return;
    }

    this.setState({navigate: `/forum/${this.state.forum.id}/new`});
  }

  onEditTopic(e:any, id:string) {
    e.stopPropagation();
    this.setState({navigate: `/forum/${this.state.forum.id}/${id}/edit`});
  }

  onLoadMore() {
    this.loadMoreTopics();
  }

  renderTopics() {
    let now = new Date();

    let divs = [];

    for (let i = 0; i < this.state.topics.length; i++) {
      let topic = this.state.topics[i];
      let author = Server.public.getProfile(topic.author);
      let date = new Date(topic.date);
      let dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + 
                    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      if(!author) continue;
      let recentAuthor = '';
      let recentDate = '';
      let allowEdit = topic.author == Server.user.getId() && !this.state.forum.archived;

      if(topic.recent) {
        recentAuthor = Server.public.getProfile(topic.recent.author).name;
        let d = new Date(topic.recent.date);
        recentDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' at ' + 
                      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      }
  
      divs.push(
        <div key={i} id="ForumTopicPanel" className="site-page-panel" style={{cursor: 'pointer', position: 'relative'}} onClick={()=>this.onViewTopic(topic.id)} data-archived={topic.archived ? 'true' : 'false'}>
          <div className="site-page-column" style={{rowGap: '5px'}}>
            <div className="site-page-row" style={{columnGap: '5px'}}>
              {topic.archived && <BsFillArchiveFill style={{color: 'var(--notification-background-color)', marginBottom: '2px'}} />}
              <div className="site-page-title-text">{topic.title}</div>
            </div>
            <div className="site-page-subtext">{recentAuthor} • {recentDate}</div>
          </div>
          {allowEdit && 
            <div id="ActionButton"style={{position: 'absolute', top: '10px', right: '10px'}} onClick={(e)=>this.onEditTopic(e, topic.id)}>
              <BsPencilSquare size="1.2em"/>
            </div>
          }
        </div>
      );
    }

    if(divs.length == 0) {
      return <div className="site-page-panel">No topics yet.</div>;
    }

    return divs;
  }

  renderLoading() {
    let message = this.state.loadError ? 'Forum not found' : 'Loading...';
    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <PageHeader text={message} navigate="/forums" />
        </PageEnvelope>
      </div>
    )
  }

  render() {
    if(this.state.navigate != '') 
      return <Navigate to={this.state.navigate} state={{from:'forum'}} />;

    if(this.state.loading || !this.state.forum)
      return this.renderLoading();

    let allowNewTopic = !this.state.loading && !this.state.forum.archived;

    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <div className="site-page-column">

            <PageHeader text={this.state.forum.title} navigate="/forums" force={true} onBack={()=>this.setState({navigate: '/forums'})}>
              <div style={{whiteSpace: 'nowrap', cursor: 'pointer'}} onClick={()=>this.onNewTopic()}><BsPlus style={{transform: 'translateY(3px)'}} />New Topic</div>
            </PageHeader>

            {this.state.forum.archived && 
              <div className="site-page-header" style={{justifyContent: 'center', backgroundColor: 'var(--notification-background-color)'}}>
                This forum has been archived.
              </div>
            }

            <div className="site-page-column">
              {this.renderTopics()}
            </div>

            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              {this.state.noMoreTopics || (this.state.loading && this.state.topics.length == 0)
                ? <div>&nbsp;</div>
                : <button onClick={()=>this.onLoadMore()} disabled={this.state.loadingMore}>
                    {this.state.loadingMore ? 'Loading...' : 'Load More'}
                  </button>
              }
              {this.state.topics.length > 5 &&
                <button disabled={!allowNewTopic} onClick={()=>this.onNewTopic()}>New Topic</button>
              }
            </div>

          </div>
        </PageEnvelope>

        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
      </div>
    );
  }
}

export default ForumPage;